import { tokenUtil } from "../../util/TokenUtil";
import { persistAuthTokens } from "../../util/authResume";

export const AUTH_V2_ENDPOINTS = {
  login: "/auth/login",
  resume: "/auth/resume",
  create: "/auth/create",
  eddLogin: "/auth/edd/login",
  eddResume: "/auth/edd/resume",
};

const extractResponseMessage = (response) => {
  if (!response) {
    return "Unable to reach authentication service.";
  }

  if (typeof response?.data === "string") {
    return response.data;
  }

  return (
    response?.data?.customMessage ||
    response?.data?.message ||
    response?.data?.error ||
    response?.statusText ||
    "Authentication request failed."
  );
};

const assertActionToken = (actionToken) => {
  if (!actionToken) {
    throw new Error("Action token is required.");
  }
};

const postAuthV2 = async ({ endpoint, payload = {}, actionToken = null }) => {
  const response = await tokenUtil.callAPI({
    endPoint: endpoint,
    method: "POST",
    reqParam: payload,
    token: actionToken,
  });

  if (response?.status === 200) {
    return response;
  }

  throw new Error(extractResponseMessage(response));
};

export const normalizeLoginV2Response = (response) => ({
  ...response,
  data: {
    ...(response?.data || {}),
    email: response?.data?.email || "",
    actionToken: response?.data?.actionToken || "",
    applications: Array.isArray(response?.data?.applications) ? response.data.applications : [],
    createOptions: Array.isArray(response?.data?.createOptions) ? response.data.createOptions : [],
  },
});

export const loginV2 = async (payload) => {
  const response = await postAuthV2({
    endpoint: AUTH_V2_ENDPOINTS.login,
    payload,
  });

  return normalizeLoginV2Response(response);
};

export const normalizeEddLoginV2Response = (response) => ({
  ...response,
  data: {
    ...(response?.data || {}),
    email: response?.data?.email || "",
    actionToken: response?.data?.actionToken || "",
    eddRequests: Array.isArray(response?.data?.eddRequests) ? response.data.eddRequests : [],
  },
});

export const eddLoginV2 = async (payload) => {
  const response = await postAuthV2({
    endpoint: AUTH_V2_ENDPOINTS.eddLogin,
    payload,
  });

  return normalizeEddLoginV2Response(response);
};

export const resumeV2 = (actionToken, applicationId) => {
  assertActionToken(actionToken);

  if (!applicationId) {
    throw new Error("Application id is required.");
  }

  return postAuthV2({
    endpoint: AUTH_V2_ENDPOINTS.resume,
    actionToken,
    payload: {
      application_id: applicationId,
    },
  });
};

export const eddResumeV2 = (actionToken, eddRequestId) => {
  assertActionToken(actionToken);

  if (!eddRequestId) {
    throw new Error("EDD request id is required.");
  }

  return postAuthV2({
    endpoint: AUTH_V2_ENDPOINTS.eddResume,
    actionToken,
    payload: {
      eddRequest_id: eddRequestId,
    },
  });
};

export const createV2 = (actionToken, createOptionPayload) => {
  assertActionToken(actionToken);

  if (!createOptionPayload?.formType) {
    throw new Error("Create form type is required.");
  }

  return postAuthV2({
    endpoint: AUTH_V2_ENDPOINTS.create,
    actionToken,
    payload: createOptionPayload,
  });
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

export const hasFinalSessionTokens = (response) => {
  const { bearerToken, refreshToken, expiry } = response?.data || {};
  return hasValue(bearerToken) && hasValue(refreshToken) && hasValue(expiry);
};

export const hasBearerAuthToken = (response) => hasValue(response?.data?.bearerToken);

export const persistFinalAuthResponse = async (
  response,
  populateUserContent,
  { requireSessionTokens = true, activeApplicationId = "" } = {}
) => {
  const { bearerToken, refreshToken, expiry } = response?.data || {};
  const responseApplicationId = (
    response?.data?.userContent?.application_id ||
    response?.data?.userContent?._id ||
    activeApplicationId ||
    ""
  );

  if (!hasValue(bearerToken) || (requireSessionTokens && (!hasValue(refreshToken) || !hasValue(expiry)))) {
    throw new Error("Final authentication response is missing session tokens.");
  }

  persistAuthTokens({
    bearerToken,
    refreshToken: hasValue(refreshToken) ? refreshToken : "",
    expiry: hasValue(expiry) ? expiry : "",
    activeApplicationId: responseApplicationId,
  });

  if (typeof populateUserContent === "function") {
    await populateUserContent();
  }

  return response;
};
